const ENV_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

/**
 * Load Google Maps JavaScript API script.
 * - Supports per-store API key (falls back to REACT_APP_GOOGLE_MAPS_API_KEY).
 * - Loads once and reuses across all components.
 * - Includes places + drawing libraries so DeliveryZoneEditor works too.
 * - If called with a different key than what's loaded, reloads the script.
 */
let loadPromise: Promise<void> | null = null;
let loadedKey: string | null = null;

export const loadGoogleMaps = (storeApiKey?: string | null): Promise<void> => {
  const apiKey = storeApiKey || ENV_API_KEY;

  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  // Already loaded with same key and all required libraries
  if (loadedKey === apiKey && window.google?.maps && (window.google.maps as any).drawing) {
    return Promise.resolve();
  }

  // If key changed, force reload
  if (loadedKey && loadedKey !== apiKey) {
    loadPromise = null;
    loadedKey = null;
  }

  // If script is already loading with same key, return the same promise
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<void>((resolve, reject) => {
    // Remove any existing script
    const existing = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existing) {
      existing.remove();
      delete (window as any).google;
    }

    const script = document.createElement('script');
    // language=el pins Places/Geocoder results to GREEK regardless of the
    // browser's locale. Without it, a store created from an English-locale
    // browser got transliterated addresses ("Leoforos Ikarou 60, Nea
    // Alikarnassos") saved in the DB — and printed like that on receipts.
    // region=GR additionally biases result ranking to Greece.
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,drawing&language=el&region=GR`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      loadedKey = apiKey;
      resolve();
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
};

/** Default fallback center (Athens, Greece) */
export const ATHENS_CENTER = { lat: 37.9838, lng: 23.7275 };

/** Get store center or fallback to Athens */
export const getStoreCenter = (store?: { latitude?: number | string | null; longitude?: number | string | null } | null) => {
  const lat = store?.latitude != null ? parseFloat(String(store.latitude)) : NaN;
  const lng = store?.longitude != null ? parseFloat(String(store.longitude)) : NaN;
  if (isFinite(lat) && isFinite(lng)) {
    return { lat, lng };
  }
  return ATHENS_CENTER;
};
